// src/pages/invoicing/ti-print.js
import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import API_BASE_URL from "../../config";

const TIPrint = () => {
  const { id } = useParams();
  const [invoice, setInvoice] = useState(null);
  const [lineItems, setLineItems] = useState([]);
  const [brand, setBrand] = useState(null);
  const [billing, setBilling] = useState(null);

  // Helper: shrink font until it fits one A4 page, then print
  const autoFitAndPrint = () => {
    const root = document.querySelector(".print-root");
    if (!root) return;

    // A4 page height in CSS pixels (96px = 1 inch)
    const pageHeightPx = Math.round((297 / 25.4) * 96); // ≈1122px

    // Start at 100%, shrink in 5% steps down to 50% max
    let scale = 100;
    root.style.fontSize = `${scale}%`;

    const fit = () => {
      let guard = 0; // safety
      while (root.scrollHeight > pageHeightPx && scale > 40 && guard < 30) {
        scale -= 5;
        root.style.fontSize = `${scale}%`;
        guard++;
      }
      window.print();
    };

    // Wait a tick so images/logos layout before measuring
    requestAnimationFrame(() => setTimeout(fit, 50));
  };

  useEffect(() => {
    fetchInvoiceData(id);
  }, [id]);

  useEffect(() => {
    if (invoice && billing) {
      document.title = invoice.ti_no; // filename for PDF
      autoFitAndPrint();
    }
  }, [invoice, billing]);

  useEffect(() => {
    const handleAfterPrint = () => {
      const root = document.querySelector(".print-root");
      if (root) root.style.fontSize = ""; // back to normal
    };
    window.addEventListener("afterprint", handleAfterPrint);
    return () => window.removeEventListener("afterprint", handleAfterPrint);
  }, []);

  const fetchInvoiceData = async (ti_id) => {
    try {
      // main TI
      const tiRes = await fetch(`${API_BASE_URL}/invoicing/get_ti/${ti_id}`, {
        credentials: "include",
      });
      const tiData = await tiRes.json();
      if (tiData.status !== "success") throw new Error("Tax Invoice not found");
      setInvoice(tiData.invoice);

      // items
      const itemRes = await fetch(
        `${API_BASE_URL}/invoicing/get_ti_items/${ti_id}`,
        { credentials: "include" },
      );
      const itemData = await itemRes.json();
      if (itemData.status === "success") setLineItems(itemData.items);

      // brand
      if (tiData.invoice.brand_id) {
        const brandRes = await fetch(
          `${API_BASE_URL}/invoicing/get_brand_by_id/${tiData.invoice.brand_id}`,
          { credentials: "include" },
        );
        const brandData = await brandRes.json();
        if (brandData.status === "success") setBrand(brandData.brand);
      }

      // billing
      const billingRes = await fetch(
        `${API_BASE_URL}/invoicing/get_billing_address`,
        { credentials: "include" },
      );
      const billingData = await billingRes.json();
      if (billingData.status === "success")
        setBilling(billingData.billing_address);
    } catch (err) {
      console.error("Failed to fetch tax invoice:", err);
    }
  };

  return (
    <div
      className="print-root"
      style={{
        width: "210mm",
        padding: "12mm",
        margin: "0 auto",
        boxSizing: "border-box",
        background: "#fff",
      }}
    >
      <style>
        {`
        body {
          font-family: Arial, sans-serif;
          color: #000;
          -webkit-print-color-adjust: exact;
          background-color: #fff;
        }
        h3, h6, strong { color: ${brand?.color_code || "#000"}; }
        img { max-width: 250px; object-fit: contain; }
        .d-flex { display: flex; justify-content: space-between; }
        .text-right { text-align: right; }
        .text-left { text-align: left; }
        table { width: 100%; font-size: 14px; border-collapse: collapse; }

        @media print {
  @page { size: A4; margin: 0; }

  html, body {
    height: auto !important;
    margin: 0 !important;
    padding: 0 !important;
  }

  .print-root,
  .invoice-box {
    break-inside: avoid-page;
    page-break-inside: avoid;
    page-break-before: avoid;
    page-break-after: avoid;
  }
 .print-root { padding: 10mm !important; }  /* was 8mm — adds breathing room */
.invoice-box * { line-height: 1.25 !important; } /* was 1.15 — a bit taller lines */

.invoice-box h3 { margin: 3mm 0 2mm !important; font-size: 20px !important; }
.invoice-box h6 { margin: 2mm 0 !important; font-size: 15px !important; }
.invoice-box p  { margin: 2mm 0 !important; font-size: 15px !important;} 

.invoice-box .p-3 { padding: 8px !important; }
.invoice-box .p-4 { padding: 10px !important; } 
.invoice-box .mt-4 { margin-top: 8px !important; }
.invoice-box .mb-3 { margin-bottom: 8px !important; }

}
      `}
      </style>

      {invoice && billing && (
        <div className="invoice-box">
          {/* Header */}
          <div className="bg-light p-3">
            <div>
              <h3
                className="font-weight-bold text-uppercase mb-1"
                style={{ color: "#003366" }}
              >
                TAX INVOICE
              </h3>
              <div className="d-flex justify-content-between align-items-start mb-3">
                <div style={{ maxWidth: "70%" }}>
                  <h6
                    className="font-weight-bold text-uppercase mb-1"
                    style={{
                      color: brand?.color_code || "#000",
                      fontWeight: "bold",
                    }}
                  >
                    {billing.company_name}
                  </h6>

                  <p className="mb-1">{billing.address}</p>
                  <p className="mb-1">
                    {billing.state}, {billing.city} - {billing.pincode}
                  </p>
                  <p className="mb-1">GSTIN: {billing.gst}</p>
                  <p className="mb-1">PAN: {billing.pan}</p>
                  <p className="mb-1">CIN: {billing.cin}</p>
                  <p className="mb-1">{billing.email}</p>
                  <p className="mb-0">{billing.mobile}</p>
                </div>

                {brand?.logo && (
                  <img
                    src={
                      brand.logo.startsWith("http")
                        ? brand.logo
                        : `${API_BASE_URL.replace("/index.php", "")}/${brand.logo}`
                    }
                    alt={brand.brand_name}
                    style={{
                      maxHeight: "200px",
                      maxWidth: "250px",
                      width: "auto",
                      height: "auto",
                      objectFit: "contain",
                      display: "block",
                    }}
                  />
                )}
              </div>
            </div>
          </div>

          {/* Bill To + TI Info */}
          <div className="mt-3 p-3">
            <div className="d-flex justify-content-between align-items-start mb-3">
              <div style={{ maxWidth: "60%" }}>
                <h6
                  className="font-weight-bold text-uppercase mb-1"
                  style={{ color: brand?.color_code, fontWeight: "bold" }}
                >
                  TO,
                </h6>

                <p
                  className="mb-1 text-uppercase"
                  style={{ fontWeight: "bold", color: "#003366" }}
                >
                  {invoice.to_company}
                </p>
                <p className="mb-1" style={{ color: "#003366" }}>
                  {invoice.to_address}
                </p>
                <p className="mb-1" style={{ color: "#003366" }}>
                  {invoice.to_city}, {invoice.to_state} - {invoice.to_pincode}
                </p>
                {invoice.to_gstin && (
                  <p className="mb-0" style={{ color: "#003366" }}>
                    GSTIN: {invoice.to_gstin}
                  </p>
                )}
              </div>

              <div className="text-left" style={{ marginRight: "50px" }}>
                <p className="mb-1">
                  <strong style={{ color: brand?.color_code }}>No</strong>
                  :<br />{" "}
                  <span style={{ color: "#003366" }}>{invoice.ti_no}</span>
                </p>
                <p className="mb-0">
                  <strong style={{ color: brand?.color_code }}>Date</strong>
                  : <br />
                  <span style={{ color: "#003366" }}>
                    {" "}
                    {new Date(invoice.created_on).toLocaleDateString("en-IN", {
                      day: "2-digit",
                      month: "short",
                      year: "numeric",
                    })}
                  </span>
                </p>
              </div>
            </div>
          </div>

          {/* Items */}
          <div className="px-2">
            <table className="w-100" style={{ fontSize: "15px" }}>
              <thead>
                <tr>
                  <td colSpan="5">
                    <hr
                      style={{
                        borderTop: "2px dotted #999",
                        margin: "4px 0 10px 0",
                        borderBottom: "none",
                      }}
                    />
                  </td>
                </tr>
                <tr
                  style={{
                    color: brand?.color_code || "#FF6600",
                    fontWeight: "bold",
                    textTransform: "uppercase",
                  }}
                >
                  <th className="text-left" style={{ width: "7%" }}>
                    No
                  </th>
                  <th className="text-left" style={{ width: "9%" }}>
                    SAC
                  </th>
                  <th className="text-left" style={{ width: "58%" }}>
                    Description
                  </th>
                  <th className="text-left" style={{ width: "12%" }}>
                    Tax (%)
                  </th>
                  <th className="text-right" style={{ width: "13%" }}>
                    Amount
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td colSpan="5">
                    <hr
                      style={{
                        borderTop: "2px dotted #999",
                        margin: "4px 0 10px 0",
                        borderBottom: "none",
                      }}
                    />
                  </td>
                </tr>
                {lineItems.map((item, index) => (
                  <tr
                    key={index}
                    style={{ color: "#003366", verticalAlign: "top" }}
                  >
                    <td className="text-left">{index + 1}</td>
                    <td className="text-left">{item.sac_code}</td>
                    <td className="text-left" style={{ paddingRight: "10px" }}>
                      {item.title}
                    </td>
                    <td className="text-left">{parseInt(item.gst_percent)}</td>
                    <td className="text-right">
                      ₹{parseFloat(item.amount).toLocaleString("en-IN")}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Totals + Bank */}
          <div
            className="bg-light p-4 mt-4 d-flex justify-content-between align-items-start"
            style={{ fontSize: "15px" }}
          >
            <div style={{ maxWidth: "60%" }}>
              <p className="font-weight-bold mb-2" style={{ color: "#003366" }}>
                <strong>For NEFT / IMPS</strong>
              </p>
              <p className="mb-1 font-weight-bold">{billing?.company_name}</p>
              <p className="mb-1">ICICI Bank</p>
              <p className="mb-1">Vashi Branch</p>
              <p className="mb-1">A/C: 015105013645</p>
              <p className="mb-0">IFSC: ICIC0000151</p>
            </div>

            <div
              className="bg-light px-4 py-4 text-right"
              style={{
                minWidth: "250px",
                fontSize: "15px",
                color: "#003366",
                textAlign: "right",
              }}
            >
              <div className="font-weight-bold mb-2">
                SUBTOTAL: ₹
                {parseFloat(invoice.subtotal).toLocaleString("en-IN")}
              </div>

              {parseFloat(invoice.igst) > 0 ? (
                <div className="mb-2">
                  IGST on {parseFloat(invoice.subtotal).toLocaleString("en-IN")}{" "}
                  @{" "}
                  {Math.round(
                    (parseFloat(invoice.igst) * 100) /
                      parseFloat(invoice.subtotal),
                  )}
                  % : ₹{parseFloat(invoice.igst).toLocaleString("en-IN")}
                </div>
              ) : (
                <>
                  <div className="mb-2">
                    CGST on{" "}
                    {parseFloat(invoice.subtotal).toLocaleString("en-IN")} @{" "}
                    {Math.round(
                      (parseFloat(invoice.cgst) * 100) /
                        parseFloat(invoice.subtotal),
                    )}
                    % : ₹{parseFloat(invoice.cgst).toLocaleString("en-IN")}
                  </div>
                  <div className="mb-2">
                    SGST on{" "}
                    {parseFloat(invoice.subtotal).toLocaleString("en-IN")} @{" "}
                    {Math.round(
                      (parseFloat(invoice.sgst) * 100) /
                        parseFloat(invoice.subtotal),
                    )}
                    % : ₹{parseFloat(invoice.sgst).toLocaleString("en-IN")}
                  </div>
                </>
              )}

              <div className="my-2">
                <hr
                  style={{ borderTop: "2px dotted #999", margin: "10px 0" }}
                />
              </div>

              <div
                className="font-weight-bold text-uppercase"
                style={{ fontSize: "14px" }}
              >
                Total Amount
              </div>

              <div
                className="font-weight-bolder"
                style={{
                  fontSize: "22px",
                  color: brand?.color_code || "#FF6600",
                }}
              >
                ₹
                <strong>
                  {Math.round(parseFloat(invoice.grand_total)).toLocaleString(
                    "en-IN",
                  )}
                </strong>
              </div>
            </div>
          </div>

          {/* Paid stamp & Against PI */}
          {/* Footer: Paid stamp + Against PI (kept together) */}
          {(invoice?.status === "Paid" || invoice?.reference_pi_no) && (
            <div className="no-break" style={{ marginTop: "8px" }}>
              {invoice?.status === "Paid" && (
                <div className="d-flex align-items-center justify-content-between">
                  {/* Left: PAID */}
                  <img
                    src="assets/logos/paid.png"
                    alt="PAID"
                    style={{ width: "100px", opacity: 0.9 }}
                  />
                  {/* Right: Company Stamp */}
                  <img
                    src="assets/logos/stamp.png"
                    alt="Company Stamp"
                    style={{ width: "150px", opacity: 0.85 }}
                  />
                </div>
              )}

              {invoice?.reference_pi_no && (
                <p
                  className="mb-0 font-weight-bold"
                  style={{ color: "#003366", marginTop: "6px" }}
                >
                  Against {invoice.reference_pi_no}
                </p>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default TIPrint;
